/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import { toJS } from 'mobx';
import Framework from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';

const GraphParamsModel = types
  .model('GraphParamsModel', {
    loading: types.optional(types.boolean, false),
    hasSavedConfig: types.optional(types.boolean, false),
    params: types.maybeNull(types.frozen()),
    pendingSaveParams: types.maybeNull(types.frozen()),
    error: types.maybeNull(types.string),
    // Track which graph the current params belong to
    loadedGraphId: types.maybeNull(types.string),
    loadedTenantId: types.maybeNull(types.string)
  })
  .views((self) => ({
    get paramsData() {
      return {
        loading: self.loading,
        hasSavedConfig: self.hasSavedConfig,
        params: self.params ? toJS(self.params) : null,
        error: self.error
      };
    },
    // Check if params are already loaded for a specific graph (and tenant context for cache)
    // Only returns true if we have cached params - if params are null/empty, API should be called again
    hasParamsForGraph(graphId, tenantId) {
      const graphIdStr = graphId ? String(graphId) : null;
      const tenantIdStr = tenantId ? String(tenantId) : null;
      return (
        self.loadedGraphId === graphIdStr &&
        self.loadedTenantId === tenantIdStr &&
        self.params !== null &&
        !self.loading
      );
    }
  }))
  .actions((self) => ({
    // Fetch graph params
    getGraphParams(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loading: true,
        error: null,
        loadedGraphId: payload.graphId ? String(payload.graphId) : null,
        loadedTenantId: payload.tenantId ? String(payload.tenantId) : null
      });
      Framework.getEventManager().publish(constants.GET_GRAPH_PARAMS, { payload });
    },

    // Save graph params
    saveGraphParams(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loading: true,
        error: null,
        pendingSaveParams: payload.params,
        loadedGraphId: payload.graphId ? String(payload.graphId) : null,
        loadedTenantId: payload.tenantId ? String(payload.tenantId) : null
      });
      Framework.getEventManager().publish(constants.SAVE_GRAPH_PARAMS, { payload });
    },

    // Delete graph params
    deleteGraphParams(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loading: true,
        error: null
      });
      Framework.getEventManager().publish(constants.DELETE_GRAPH_PARAMS, { payload });
    },

    // Set params response
    setGraphParams(data) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loading: false,
        params: data?.params || null,
        hasSavedConfig: !!data?.params
      });
      Framework.getEventManager().publish(constants.GET_GRAPH_PARAMS_SUCCESS, { params: data });
    },

    // Set save success
    setSaveSuccess(data) {
      const snapshot = getSnapshot(self);
      applySnapshot(self, {
        ...snapshot,
        loading: false,
        hasSavedConfig: true,
        params: snapshot.pendingSaveParams || snapshot.params,
        pendingSaveParams: null
      });
      Framework.getEventManager().publish(constants.SAVE_GRAPH_PARAMS_SUCCESS, { data });
    },

    // Set delete success
    setDeleteSuccess(data) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loading: false,
        hasSavedConfig: false,
        params: null
      });
      Framework.getEventManager().publish(constants.DELETE_GRAPH_PARAMS_SUCCESS, { data });
    },

    // Set error for any operation
    setError(errorMessage, operation = 'get') {
      applySnapshot(self, {
        ...getSnapshot(self),
        loading: false,
        error: errorMessage
      });
      if (operation === 'save') {
        Framework.getEventManager().publish(constants.SAVE_GRAPH_PARAMS_ERROR, { error: errorMessage });
      } else if (operation === 'delete') {
        Framework.getEventManager().publish(constants.DELETE_GRAPH_PARAMS_ERROR, { error: errorMessage });
      } else {
        Framework.getEventManager().publish(constants.GET_GRAPH_PARAMS_ERROR, { error: errorMessage });
      }
    },

    // Reset state (clears all data including cache)
    resetState() {
      applySnapshot(self, {
        loading: false,
        hasSavedConfig: false,
        params: null,
        pendingSaveParams: null,
        error: null,
        loadedGraphId: null,
        loadedTenantId: null
      });
    }
  }));

export default GraphParamsModel;
